import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, doc, getDoc, serverTimestamp, updateDoc, query, orderBy } from 'firebase/firestore';
import { validateCheckoutForm, validateQuantity, validatePrice, sanitizeInput } from '../utils/validation';
import { createPayment } from '../services/skipcash';
import Modal from '../components/Modal';
import './Checkout.css';

function Checkout() {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    buildingNumber: '',
    streetNumber: '',
    zoneNumber: '',
    shippingMethod: '', // Changed from city to shippingMethod
    country: 'Qatar',
    paymentMethod: 'skipcash',
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
  const [deliveryFee, setDeliveryFee] = useState(50);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [isLoadingShippingMethods, setIsLoadingShippingMethods] = useState(false);

  // Fetch shipping methods from Firestore
  useEffect(() => {
    const fetchShippingMethods = async () => {
      setIsLoadingShippingMethods(true);
      try {
        const shippingMethodsRef = collection(db, 'shippingMethods');
        const q = query(shippingMethodsRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        const shippingMethodsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setShippingMethods(shippingMethodsData);
        
        // Set default delivery fee from settings (fallback)
        const settingsRef = doc(db, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          const fee = settingsDoc.data().deliveryFee;
          if (fee !== undefined && fee !== null) {
            const numericFee = Number(fee);
            if (!Number.isNaN(numericFee)) {
              setDeliveryFee(numericFee);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch shipping methods:', error);
        setShippingMethods([]);
      } finally {
        setIsLoadingShippingMethods(false);
      }
    };
    fetchShippingMethods();
  }, []);

  const subtotal = getCartTotal();
  
  // Calculate shipping fee based on selected shipping method
  const selectedShippingMethod = shippingMethods.find(m => m.id === formData.shippingMethod);
  const shipping = selectedShippingMethod ? selectedShippingMethod.fee : deliveryFee;
  const total = subtotal + shipping;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = sanitizeInput(value, name === 'email' ? 255 : name.includes('address') || name.includes('city') ? 500 : 255);
    setFormData((prev) => ({
      ...prev,
      [name]: sanitized,
    }));
    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Generate a random, unique order number (client-safe, no Firestore reads)
  const getNextOrderNumber = async () => {
    const timePart = Date.now().toString(36).toUpperCase();
    const digits = '0123456789';
    const length = 6;
    const cryptoObj = typeof window !== 'undefined' ? window.crypto : null;
    let randomPart = '';
    if (cryptoObj?.getRandomValues) {
      const values = new Uint32Array(length);
      cryptoObj.getRandomValues(values);
      randomPart = Array.from(values, value => digits[value % digits.length]).join('');
    } else {
      for (let i = 0; i < length; i += 1) {
        randomPart += digits[Math.floor(Math.random() * digits.length)];
      }
    }

    return `ORDER-${timePart}-${randomPart}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // 1. Validate form inputs
      const formValidation = validateCheckoutForm(formData);
      if (!formValidation.isValid) {
        setFormErrors(formValidation.errors);
        setIsProcessing(false);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Form Validation Error',
          message: 'Please correct the errors in the form before submitting.',
          onConfirm: () => setModal({ ...modal, isOpen: false })
        });
        return;
      }

      // 2. Validate cart items and fetch actual prices from database (CRITICAL SECURITY FIX)
      const validatedItems = [];
      let serverSubtotal = 0;
      const stockErrors = [];

      for (const cartItem of cartItems) {
        // Validate quantity
        if (!validateQuantity(cartItem.quantity)) {
          setIsProcessing(false);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Invalid Quantity',
            message: `Invalid quantity for ${cartItem.name}. Quantity must be between 1 and 100.`,
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
          return;
        }

        // Fetch product from database to get actual price and stock
        try {
          const productDoc = await getDoc(doc(db, 'products', cartItem.id));
          
          if (!productDoc.exists()) {
            setIsProcessing(false);
            setModal({
              isOpen: true,
              type: 'error',
              title: 'Product Not Found',
              message: `Product "${cartItem.name}" is no longer available.`,
              onConfirm: () => {
                setModal({ ...modal, isOpen: false });
                navigate('/cart');
              }
            });
            return;
          }

          const productData = productDoc.data();
          const actualPrice = Number(productData.price);
          const clientPrice = Number(cartItem.price);

          // SECURITY: Validate price matches database (prevent price manipulation)
          if (Math.abs(actualPrice - clientPrice) > 0.01) {
            setIsProcessing(false);
            setModal({
              isOpen: true,
              type: 'error',
              title: 'Price Mismatch',
              message: `Price for "${cartItem.name}" has changed. Please refresh your cart.`,
              onConfirm: () => {
                setModal({ ...modal, isOpen: false });
                navigate('/cart');
              }
            });
            return;
          }

          // SECURITY: Validate stock availability (prevent overselling)
          if (productData.trackQuantity !== false && productData.quantity !== undefined) {
            const availableStock = Number(productData.quantity) || 0;
            if (cartItem.quantity > availableStock) {
              stockErrors.push(`${cartItem.name}: Only ${availableStock} available, but ${cartItem.quantity} requested`);
            }
          }

          // Use server price (not client price)
          const itemTotal = actualPrice * cartItem.quantity;
          serverSubtotal += itemTotal;

          const sanitizedNotes = sanitizeInput(cartItem.notes, 500);
          const validatedItem = {
            id: cartItem.id,
            name: cartItem.name,
            price: actualPrice, // Use server price
            quantity: cartItem.quantity,
            size: cartItem.selectedSize,
            color: cartItem.selectedColor,
            length: cartItem.selectedLength || '',
            image: cartItem.image,
          };

          if (sanitizedNotes) {
            validatedItem.notes = sanitizedNotes;
          }

          validatedItems.push(validatedItem);
        } catch (error) {
          console.error(`Error validating product ${cartItem.id}:`, error);
          setIsProcessing(false);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Validation Error',
            message: `Error validating product "${cartItem.name}". Please try again.`,
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
          return;
        }
      }

      // Check for stock errors
      if (stockErrors.length > 0) {
        setIsProcessing(false);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Insufficient Stock',
          message: stockErrors.join('\n'),
          onConfirm: () => {
            setModal({ ...modal, isOpen: false });
            navigate('/cart');
          }
        });
        return;
      }

      // 3. Recalculate totals server-side (CRITICAL SECURITY FIX)
      // Get shipping fee from selected shipping method
      if (!formData.shippingMethod) {
        setIsProcessing(false);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Shipping Method Required',
          message: 'Please select a shipping method.',
          onConfirm: () => setModal({ ...modal, isOpen: false })
        });
        return;
      }

      // Fetch shipping method from Firestore to get server-side fee
      const shippingMethodRef = doc(db, 'shippingMethods', formData.shippingMethod);
      const shippingMethodDoc = await getDoc(shippingMethodRef);
      
      if (!shippingMethodDoc.exists()) {
        setIsProcessing(false);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Invalid Shipping Method',
          message: 'Selected shipping method not found. Please refresh and try again.',
          onConfirm: () => {
            setModal({ ...modal, isOpen: false });
            window.location.reload();
          }
        });
        return;
      }

      const shippingMethodData = shippingMethodDoc.data();
      const serverShipping = Number(shippingMethodData.fee) || 0;
      const serverTotal = Math.round((serverSubtotal + serverShipping) * 100) / 100; // Round to 2 decimals

      // Validate totals match (with small tolerance for rounding)
      if (Math.abs(total - serverTotal) > 0.01) {
        setIsProcessing(false);
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Total Mismatch',
          message: 'Order total has changed. Please refresh and try again.',
          onConfirm: () => {
            setModal({ ...modal, isOpen: false });
            navigate('/cart');
          }
        });
        return;
      }

      // 4. Generate order ID and secure receipt token
      const orderId = await getNextOrderNumber();
      // Generate a random secure token for receipt URL (hard to guess)
      const receiptToken = crypto.randomUUID ? crypto.randomUUID() : 
        `${Date.now()}-${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;

      // 5. Prepare order data - store in pendingOrders (NOT in orders collection yet)
      // Order will only be created in "orders" collection when payment is confirmed
      const pendingOrderData = {
        orderId: orderId,
        receiptToken: receiptToken,
        customer: {
          firstName: sanitizeInput(formData.firstName, 100),
          lastName: sanitizeInput(formData.lastName, 100),
          email: sanitizeInput(formData.email, 255),
          phone: sanitizeInput(formData.phone, 20),
        },
        shipping: {
          buildingNumber: sanitizeInput(formData.buildingNumber, 50),
          streetNumber: sanitizeInput(formData.streetNumber, 50),
          zoneNumber: sanitizeInput(formData.zoneNumber, 50),
          shippingMethod: formData.shippingMethod,
          shippingMethodName: selectedShippingMethod ? selectedShippingMethod.name : '',
          city: selectedShippingMethod ? selectedShippingMethod.name : '', // For backward compatibility
          country: sanitizeInput(formData.country, 100),
        },
        items: validatedItems,
        totals: {
          subtotal: Math.round(serverSubtotal * 100) / 100,
          shipping: serverShipping,
          total: serverTotal,
        },
        paymentMethod: formData.paymentMethod,
        createdAt: serverTimestamp(),
        // Security metadata
        validatedAt: serverTimestamp(),
        clientSubtotal: subtotal, // For audit purposes
        serverSubtotal: serverSubtotal, // Actual server-calculated total
      };

      // 6. Save to pendingOrders collection (temporary - will be moved to orders when paid)
      const pendingOrdersRef = collection(db, 'pendingOrders');
      const pendingOrderDocRef = await addDoc(pendingOrdersRef, pendingOrderData);
      const pendingOrderDocId = pendingOrderDocRef.id;

      // 7. Handle payment (only SkipCash supported)
      if (formData.paymentMethod === 'skipcash') {
        // Prepare address for SkipCash (combine building, street, zone)
        const streetAddress = `${formData.buildingNumber}, ${formData.streetNumber}, Zone ${formData.zoneNumber}`;
        
        // Prepare SkipCash payment data
        const skipCashData = {
          amount: serverTotal.toFixed(2),
          firstName: sanitizeInput(formData.firstName, 60),
          lastName: sanitizeInput(formData.lastName, 60),
          email: sanitizeInput(formData.email, 255),
          phone: sanitizeInput(formData.phone, 15),
          street: streetAddress.substring(0, 60),
          city: selectedShippingMethod ? sanitizeInput(selectedShippingMethod.name, 50) : 'Doha',
          state: 'QA', // Qatar state code
          country: 'QA', // Qatar country code
          postalCode: formData.zoneNumber.substring(0, 10) || '00000',
          transactionId: orderId,
          custom1: pendingOrderDocId, // Store pending order doc ID for webhook lookup
        };

        setIsProcessing(false);

        // Create SkipCash payment and redirect
        try {
          // Store pendingOrderDocId in custom1 for webhook lookup
          // Webhook will create actual order when payment is confirmed
          skipCashData.custom1 = pendingOrderDocId;

          // Create payment
          const paymentResponse = await createPayment(skipCashData);
          
          if (!paymentResponse.resultObj || !paymentResponse.resultObj.payUrl) {
            throw new Error('Invalid payment response');
          }

          const { payUrl, id: paymentId } = paymentResponse.resultObj;

          // Update pending order with paymentId
          try {
            const pendingOrderRef = doc(db, 'pendingOrders', pendingOrderDocId);
            await updateDoc(pendingOrderRef, {
              paymentId: paymentId,
              updatedAt: serverTimestamp(),
            });
          } catch (updateError) {
            console.error('Error updating pending order with paymentId:', updateError);
            // Continue anyway - paymentId is stored in localStorage
          }

          // Store order info in sessionStorage and localStorage for receipt page
          const orderInfo = {
            orderId,
            pendingOrderDocId, // Store pending order ID instead
            paymentId,
            receiptToken,
            timestamp: Date.now(),
          };
          sessionStorage.setItem('pendingOrder', JSON.stringify(orderInfo));
          localStorage.setItem(`order_${orderId}`, JSON.stringify(orderInfo));

          // Append return URL to SkipCash payment URL if supported
          // SkipCash will redirect back to this URL after payment
          const returnParams = new URLSearchParams({
            token: receiptToken,
            orderId: orderId,
            paymentId: paymentId,
          });
          const returnUrl = `${window.location.origin}/processing-payment?${returnParams.toString()}`;
          const paymentUrlWithReturn = `${payUrl}${payUrl.includes('?') ? '&' : '?'}returnUrl=${encodeURIComponent(returnUrl)}`;

          // Redirect to SkipCash payment page
          window.location.href = paymentUrlWithReturn;
        } catch (error) {
          console.error('Error creating payment:', error);
          
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Payment Error',
            message: error.message || 'Failed to initiate payment. Please try again.',
            onConfirm: () => {
              setModal({ ...modal, isOpen: false });
              setIsProcessing(false);
            },
          });
        }
      } else {
        // Invalid payment method
        setModal({
          isOpen: true,
          type: 'error',
          title: 'Invalid Payment Method',
          message: 'Please select a valid payment method.',
          onConfirm: () => {
            setModal({ ...modal, isOpen: false });
            setIsProcessing(false);
          },
        });
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Order Error',
        message: 'Failed to place order. Please try again.',
        onConfirm: () => {
          setModal({ ...modal, isOpen: false });
          setIsProcessing(false);
        },
      });
    }
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="checkout-title">Checkout</h1>

        <form id="checkout-form" className="checkout-form" onSubmit={handleSubmit}>
          <div className="checkout-layout">
            <div className="checkout-main">
              {/* Contact Information */}
              <div className="checkout-section">
                <h2 className="section-title">Contact Information</h2>

                <div className="form-row form-row--split">
                  <div>
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Enter your first name"
                      required
                      maxLength={100}
                      className={formErrors.firstName ? 'input-error' : ''}
                      autoComplete="given-name"
                    />
                    {formErrors.firstName && (
                      <span className="error-message">{formErrors.firstName}</span>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Enter your last name"
                      required
                      maxLength={100}
                      className={formErrors.lastName ? 'input-error' : ''}
                      autoComplete="family-name"
                    />
                    {formErrors.lastName && (
                      <span className="error-message">{formErrors.lastName}</span>
                    )}
                  </div>
                </div>

                <div className="form-row form-row--split">
                  <div>
                    <label htmlFor="email">Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      maxLength={255}
                      className={formErrors.email ? 'input-error' : ''}
                      autoComplete="email"
                    />
                    {formErrors.email && (
                      <span className="error-message">{formErrors.email}</span>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+974 1234 5678"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      maxLength={20}
                      className={formErrors.phone ? 'input-error' : ''}
                      autoComplete="tel"
                    />
                    {formErrors.phone && (
                      <span className="error-message">{formErrors.phone}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="checkout-section">
                <h2 className="section-title">Shipping Address</h2>

                <div className="form-row form-row--split">
                  <div>
                    <label htmlFor="buildingNumber">Building Number *</label>
                    <input
                      id="buildingNumber"
                      name="buildingNumber"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.buildingNumber}
                      onChange={handleChange}
                      placeholder="e.g., 45"
                      required
                      autoComplete="address-line1"
                    />
                  </div>

                  <div>
                    <label htmlFor="streetNumber">Street Number *</label>
                    <input
                      id="streetNumber"
                      name="streetNumber"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.streetNumber}
                      onChange={handleChange}
                      placeholder="e.g., 123"
                      required
                      autoComplete="address-line2"
                    />
                  </div>
                </div>

                <div className="form-row form-row--split">
                  <div>
                    <label htmlFor="zoneNumber">Zone Number *</label>
                    <input
                      id="zoneNumber"
                      name="zoneNumber"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.zoneNumber}
                      onChange={handleChange}
                      placeholder="e.g., 12"
                      required
                      maxLength={50}
                      className={formErrors.zoneNumber ? 'input-error' : ''}
                      autoComplete="postal-code"
                    />
                    {formErrors.zoneNumber && (
                      <span className="error-message">{formErrors.zoneNumber}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <label htmlFor="shippingMethod">City *</label>
                  {isLoadingShippingMethods ? (
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0.75rem 0' }}>Loading cities...</p>
                  ) : shippingMethods.length === 0 ? (
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', padding: '0.75rem 0' }}>No cities available. Please contact support.</p>
                  ) : (
                    <div className="city-selection-grid">
                      {shippingMethods.map((method) => (
                        <label key={method.id} className="city-option">
                          <input
                            type="radio"
                            name="shippingMethod"
                            value={method.id}
                            checked={formData.shippingMethod === method.id}
                            onChange={handleChange}
                            required
                          />
                          <div className="city-option-inner">
                            <span className="city-name">{method.name}</span>
                            <span className="city-price">{method.fee?.toFixed(2)} ر.ق</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {formErrors.shippingMethod && (
                    <span className="error-message">{formErrors.shippingMethod}</span>
                  )}
                </div>

                <div className="form-row">
                  <label htmlFor="country">Country *</label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    readOnly
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="checkout-section">
                <h2 className="section-title">Payment Method</h2>

                <div className="payment-methods">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="skipcash"
                      checked={formData.paymentMethod === 'skipcash'}
                      onChange={handleChange}
                    />
                    <div className="payment-option-logo">
                      <img 
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS9DEdKURlaTIxX5TSMycnkaUEs0IwUTZlLbA&s" 
                        alt="SkipCash" 
                        style={{ maxWidth: '120px', height: 'auto' }}
                      />
                    </div>
                    <div className="payment-option-content">
                      <span className="payment-option-title">Pay with SkipCash</span>
                      <span className="payment-option-desc">Secure online payment via SkipCash</span>
                    </div>
                  </label>

                </div>
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="checkout-sidebar">
              <div className="order-summary">
                <h2 className="summary-title">Order Summary</h2>

                <div className="summary-items">
                  {cartItems.map((item, index) => (
                    <div key={`${item.id}-${item.selectedSize}-${item.selectedLength}-${item.selectedColor}-${index}`} className="summary-item">
                      <div
                        className="summary-item-image"
                        style={{
                          backgroundImage: `url(${item.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      ></div>
                      <div className="summary-item-details">
                        <p className="summary-item-name">{item.name}</p>
                        <p className="summary-item-options">
                          {[
                            item.selectedSize ? `Size: ${item.selectedSize}` : null,
                            item.selectedLength ? `Length: ${item.selectedLength}` : null,
                            item.selectedColor ? `Color: ${item.selectedColor}` : null,
                          ].filter(Boolean).join(' / ')}
                        </p>
                        {item.notes && (
                          <p className="summary-item-notes">Notes: {item.notes}</p>
                        )}
                        <p className="summary-item-quantity">Qty: {item.quantity}</p>
                      </div>
                      <p className="summary-item-price">{(item.price * item.quantity).toFixed(2)} ر.ق</p>
                    </div>
                  ))}
                </div>

                <div className="summary-totals">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>{subtotal.toFixed(2)} ر.ق</span>
                  </div>

                  <div className="summary-row">
                    <span>Shipping</span>
                    <span>{shipping.toFixed(2)} ر.ق</span>
                  </div>

                  <div className="summary-divider"></div>

                  <div className="summary-row summary-total">
                    <span>Total</span>
                    <span>{total.toFixed(2)} ر.ق</span>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-place-order btn-place-order-regular" 
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Sticky Place Order for Mobile */}
        <div className="checkout-place-order-sticky">
          <button 
            type="button"
            className="btn btn-primary btn-place-order-sticky" 
            disabled={isProcessing}
            onClick={() => {
              const form = document.getElementById('checkout-form');
              if (form && !isProcessing) {
                // Trigger form submission
                form.requestSubmit();
              }
            }}
          >
            {isProcessing ? 'Processing...' : 'Place Order'}
          </button>
        </div>

        <Modal
          isOpen={modal.isOpen}
          onClose={() => setModal({ ...modal, isOpen: false })}
          title={modal.title}
          message={modal.message}
          type={modal.type}
          onConfirm={modal.onConfirm}
          confirmText="OK"
          showCancel={false}
        />
      </div>
    </div>
  );
}

export default Checkout;
